import { deepCopy } from '@lhb/func';
import { ControlType, parseValueCatch } from '../../DynamicComponent/config';
import { addressManualChange, addressIdentification, pcdIdentification } from '../../DynamicComponent/customize';
import { resolveFranchiseeAddress } from './franchiseeAddress';

/** 在独立快照中复用原有地址联动，避免已取消的异步请求改写当前表单。 */
export const initializeFranchiseeAddress = async ({
  address, fields, form, isCurrent, onBusinessPlan, onBusinessInfo,
}: {
  address: string;
  fields: any[];
  form: any;
  isCurrent: () => boolean;
  onBusinessPlan: (value: any) => void;
  onBusinessInfo: (value: any) => void;
}) => {
  const snapshot: any[] = deepCopy(fields);
  let active = true;
  const canPublish = () => active && isCurrent();
  const publish = (identification: string) => {
    if (!canPublish()) return;
    fields.filter(item => item.identification === identification).forEach(item => {
      const origin = snapshot.find(target => target.identification === identification && target.propertyId === item.propertyId);
      if (origin) item.textValue = origin.textValue;
    });
  };
  const addressForm = {
    setFieldValue(identification: string, value: any) {
      if (!canPublish()) return;
      publish(identification);
      form.setFieldValue(identification, value);
    },
  };
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const params = await resolveFranchiseeAddress(address, undefined);
    if (!canPublish()) return false;
    params.isCluster = fields.some(item => item.controlType === ControlType.MATCH_BUSINESS_CIRCLE.value);
    const addressTask = addressManualChange(params, { current: snapshot }, addressForm, value => {
      if (!canPublish()) return;
      snapshot.filter(item => item.controlType === ControlType.BUSINESS_PLANNING.value).forEach(item => publish(item.identification));
      onBusinessPlan(value);
    }, value => {
      if (!canPublish()) return;
      snapshot.filter(item => item.controlType === ControlType.MATCH_BUSINESS_CIRCLE.value).forEach(item => publish(item.identification));
      onBusinessInfo(value);
    }, () => {
      // 原请求封装负责提示；独立后台联动失败不清空已解析地址，也不产生未处理拒绝。
    });
    // 逆解析或地区服务无响应时同样回退为手动选择；迟到结果仍停留在独立快照。
    await Promise.race([
      addressTask,
      new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error('地址解析超时')), 12000); }),
    ]);
    if (!canPublish()) return false;
    const area = snapshot.find(item => item.identification === pcdIdentification);
    const region = area ? parseValueCatch(area) : null;
    const resolvedAddress = snapshot.find(item => item.identification === addressIdentification);
    const location = resolvedAddress ? parseValueCatch(resolvedAddress) : null;
    if (!location?.longitude || !location?.latitude || (area && (!region?.provinceId || !region?.cityId || !region?.districtId))) {
      throw new Error('地址解析失败');
    }
    publish(addressIdentification);
    publish(pcdIdentification);
    return true;
  } catch {
    active = false;
    if (!isCurrent()) return false;
    fields.forEach(item => {
      if (item.identification === pcdIdentification) item.textValue = null;
      if (item.identification === addressIdentification) item.textValue = JSON.stringify({ address });
    });
    form.setFieldValue(pcdIdentification, undefined);
    return false;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
};

